import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { FeatureFlagsService } from '@wsphere/warranties/web';
import { BehaviorSubject, switchMap, tap } from 'rxjs';
import { BfsConfigWebService, BfsEnvironmentOption } from './bfs-config.web.service';
import { hasConfiguredBfsProductMappings, isBfsCheckoutConfigured } from './bfs-configuration-status';

const ENVIRONMENT_OPTIONS: { value: BfsEnvironmentOption; label: string }[] = [
  { value: 'training', label: 'Training' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
];

interface BudcoProductMapping {
  productId: string;
  policyPrefixes: string[];
}

interface BudcoProductMappingConfig {
  version: 1;
  mappings: BudcoProductMapping[];
}

type LegacyBudcoProductMapping = {
  productId?: unknown;
  policyPrefixes?: unknown;
};

interface BfsStatus {
  configured: boolean;
  credentialsConfigured: boolean;
  dealershipConfigured: boolean;
  productMappingsConfigured: boolean;
  enabled: boolean;
  environment: BfsEnvironmentOption;
  environmentOverridden: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-bfs-dialog',
  templateUrl: './bfs.dialog.html',
  standalone: false,
})
export class BfsDialog implements OnInit {
  private readonly bfsConfigSvc = inject(BfsConfigWebService);
  private readonly toastSvc = inject(ToastService);
  private readonly securitySvc = inject(SecurityService);
  private readonly featureFlagsSvc = inject(FeatureFlagsService);

  @Output() enabledChanged = new EventEmitter<boolean>();
  @Output() configuredChanged = new EventEmitter<boolean>();

  /** Set by the parent page when Lemur or Authorize.net is active and must be disabled first. */
  @Input() blockedReason: string | null = null;

  readonly environmentOptions = ENVIRONMENT_OPTIONS;

  saving = false;
  status$ = new BehaviorSubject<BfsStatus | null>(null);

  form = new FormGroup({
    environment: new FormControl<BfsEnvironmentOption>('staging', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    dealershipId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    productMappings: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  readonly canManageBfs$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('stf_EditSettings', permissionKeys);
    }),
    tap(canManage => (canManage ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false })))
  );

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const [configStatus, tenantFlags] = await Promise.all([
        this.bfsConfigSvc.getConfigStatus(),
        this.bfsConfigSvc.getTenantFlags(),
      ]);

      this.applyStatus({
        configured: isBfsCheckoutConfigured(configStatus, tenantFlags),
        credentialsConfigured: Boolean(configStatus?.configured),
        dealershipConfigured: Boolean(configStatus?.dealershipConfigured),
        productMappingsConfigured: hasConfiguredBfsProductMappings(tenantFlags.productMappings),
        enabled: tenantFlags.enabled,
        environment: configStatus?.environment ?? 'staging',
        environmentOverridden: Boolean(configStatus?.environmentOverridden),
      });
      this.form.patchValue(
        {
          dealershipId: tenantFlags.dealershipId,
          productMappings: this.deserializeProductMappings(tenantFlags.productMappings),
        },
        { emitEvent: false }
      );
      this.form.markAsPristine();
    } catch {
      this.enabledChanged.emit(false);
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to load Budco Financial Services (BFS) configuration',
        text: 'Refresh the page and try again. If this continues, contact support.',
      });
    }
  }

  async enable(): Promise<void> {
    if (!this.status$.value?.credentialsConfigured) {
      this.toastSvc.show({
        type: 'error',
        title: 'Budco Financial Services (BFS) credentials required',
        text: 'Configure valid Budco Financial Services (BFS) credentials for this tenant before enabling financing.',
      });
      return;
    }

    if (this.form.invalid) {
      this.toastSvc.show({
        type: 'error',
        title: 'Missing required fields',
        text: 'Enter a dealer ID and product mapping, then select an environment before enabling.',
      });
      return;
    }

    if (this.blockedReason) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to enable Budco Financial Services (BFS) financing',
        text: this.blockedReason,
      });
      return;
    }

    await this.run('Unable to enable Budco Financial Services (BFS) financing.', async () => {
      await this.persistTenantSettings();
      await this.bfsConfigSvc.setEnabled(true);
      return this.loadStatus();
    });
  }

  async disable(): Promise<void> {
    await this.run('Unable to disable Budco Financial Services (BFS) financing.', async () => {
      await this.bfsConfigSvc.setEnabled(false);
      return this.loadStatus();
    });
  }

  async update(): Promise<void> {
    if (this.form.invalid) {
      this.toastSvc.show({
        type: 'error',
        title: 'Missing required fields',
        text: 'Enter a dealer ID and product mapping before saving.',
      });
      return;
    }

    await this.run('Unable to update Budco Financial Services (BFS) financing.', async () => {
      await this.persistTenantSettings();
      return this.loadStatus();
    });
  }

  async remove(): Promise<void> {
    await this.run('Unable to remove the BFS dealer settings.', async () => {
      await this.bfsConfigSvc.setTenantSettings('', '');
      await this.bfsConfigSvc.setEnabled(false);
      this.form.reset({ environment: this.form.value.environment ?? 'staging', dealershipId: '', productMappings: '' });
      return this.loadStatus();
    });
  }

  async useDefaultEnvironment(): Promise<void> {
    await this.run('Unable to clear the environment override.', async () => {
      await this.bfsConfigSvc.useDefaultEnvironment();
      return this.loadStatus();
    });
  }

  private async persistTenantSettings(): Promise<void> {
    const environment = this.form.controls.environment.value;
    const dealershipId = this.form.controls.dealershipId.value.trim();
    const serializedMappings = this.serializeProductMappings(this.form.controls.productMappings.value.trim());

    await this.bfsConfigSvc.setEnvironment(environment);
    await this.bfsConfigSvc.setTenantSettings(dealershipId, serializedMappings);
  }

  private async loadStatus(): Promise<BfsStatus> {
    const [configStatus, tenantFlags] = await Promise.all([
      this.bfsConfigSvc.getConfigStatus(),
      this.bfsConfigSvc.getTenantFlags(),
    ]);

    return {
      configured: isBfsCheckoutConfigured(configStatus, tenantFlags),
      credentialsConfigured: Boolean(configStatus?.configured),
      dealershipConfigured: Boolean(configStatus?.dealershipConfigured),
      productMappingsConfigured: hasConfiguredBfsProductMappings(tenantFlags.productMappings),
      enabled: tenantFlags.enabled,
      environment: configStatus?.environment ?? 'staging',
      environmentOverridden: Boolean(configStatus?.environmentOverridden),
    };
  }

  private async run(fallback: string, action: () => Promise<BfsStatus>): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      const status = await action();
      this.applyStatus(status);
      this.featureFlagsSvc.refresh();
      this.toastSvc.show({
        type: 'success',
        title: 'Budco Financial Services (BFS) financing updated',
        text: status.enabled
          ? 'Budco Financial Services (BFS) financing is enabled for this tenant.'
          : status.configured
            ? 'Budco Financial Services (BFS) is configured but disabled. New checkout applications will not be submitted.'
            : 'Budco Financial Services (BFS) setup is incomplete. Add credentials, a dealer ID, and product mappings before enabling.',
      });
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Budco Financial Services (BFS) configuration error',
        text: this.readErrorMessage(error, fallback),
      });
    } finally {
      this.saving = false;
    }
  }

  private applyStatus(status: BfsStatus): void {
    this.status$.next(status);
    this.enabledChanged.emit(status.enabled);
    this.configuredChanged.emit(status.configured);
    this.form.patchValue({ environment: status.environment }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private readErrorMessage(error: unknown, fallback: string): string {
    const err = error as { error?: { message?: unknown }; message?: unknown };
    const message = err?.error?.message ?? err?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && message.every(item => typeof item === 'string')) {
      return message.join(' ');
    }
    return fallback;
  }

  private deserializeProductMappings(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return '';

    const parsed = this.parseMappingConfig(trimmed);
    if (!parsed) {
      if (trimmed.startsWith('{')) return '';
      return `${trimmed}: *`;
    }

    return parsed.mappings.map(mapping => `${mapping.productId}: ${mapping.policyPrefixes.join(', ')}`).join('\n');
  }

  private serializeProductMappings(raw: string): string {
    const lines = raw
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    if (!lines.length) {
      throw new Error('Invalid Budco product mapping format. Add at least one line.');
    }

    const mappings = lines.map((line, index) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex <= 0 || separatorIndex === line.length - 1) {
        throw new Error(`Invalid Budco product mapping format on line ${index + 1}. Use: product_id: PREFIX1, PREFIX2`);
      }

      const productId = line.slice(0, separatorIndex).trim();
      const policyPrefixes = line
        .slice(separatorIndex + 1)
        .split(',')
        .map(prefix => prefix.trim().toUpperCase())
        .filter(Boolean);

      if (!productId || !policyPrefixes.length) {
        throw new Error(`Invalid Budco product mapping format on line ${index + 1}. Use: product_id: PREFIX1, PREFIX2`);
      }

      return { productId, policyPrefixes };
    });

    return JSON.stringify({ version: 1, mappings } as BudcoProductMappingConfig);
  }

  private parseMappingConfig(raw: string): BudcoProductMappingConfig | null {
    if (!raw.startsWith('{')) return null;

    try {
      const parsed = JSON.parse(raw) as Partial<BudcoProductMapping> &
        Partial<BudcoProductMappingConfig> &
        LegacyBudcoProductMapping;

      const mappingsSource = Array.isArray(parsed?.mappings)
        ? parsed.mappings
        : [
            {
              productId: parsed?.productId,
              policyPrefixes: parsed?.policyPrefixes,
            },
          ];

      const mappings = mappingsSource
        .map(mapping => {
          const productId = typeof mapping?.productId === 'string' ? mapping.productId.trim() : '';
          const policyPrefixes = Array.isArray(mapping?.policyPrefixes)
            ? mapping.policyPrefixes
                .map(prefix => (typeof prefix === 'string' ? prefix.trim().toUpperCase() : ''))
                .filter(Boolean)
            : [];

          return productId && policyPrefixes.length ? { productId, policyPrefixes } : null;
        })
        .filter((mapping): mapping is BudcoProductMapping => Boolean(mapping));

      if (!mappings.length) return null;
      return { version: 1, mappings };
    } catch {
      return null;
    }
  }
}
